import { ChatConfig, ChatMessage, ChatResponse, ConversationHistory, LabelPromptMapping, DetectedLabel } from '../types';

export class ChatGPTService {
  private static async makeRequest(messages: ChatMessage[], config: ChatConfig): Promise<ChatResponse> {
    try {
      // Préparer les messages (enlever timestamp qui n'est pas nécessaire pour OpenAI)
      const cleanMessages = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      console.log('📡 Appel à chatgpt-api.php avec', cleanMessages.length, 'messages');
      
      // Appel à l'endpoint PHP qui cache la clé API
      const response = await fetch('/chatgpt-api.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: config.model,
          messages: cleanMessages,
          temperature: config.temperature,
          maxTokens: config.maxTokens,
        }),
      }); 

      console.log('📡 Réponse reçue:', response.status, response.statusText);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          const text = await response.text();
          console.error('❌ Réponse non-JSON:', text);
          throw new Error(`Erreur serveur (${response.status}): ${response.statusText}`);
        }
        console.error('❌ Erreur API:', errorData);
        throw new Error(errorData.error || errorData.message || `Erreur API: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📡 Données reçues:', data);
      
      if (!data.success) {
        throw new Error(data.error || 'Erreur lors de l\'appel à l\'API');
      }
      
      if (!data.content) {
        throw new Error('Réponse API invalide: contenu manquant');
      }
      
      return {
        content: data.content,
        usage: data.usage,
      };
    } catch (error) {
      console.error('❌ Erreur dans makeRequest:', error);
      throw error;
    }
  }

  public static async sendMessage(
    userMessage: string,
    config: ChatConfig,
    conversationHistory: ConversationHistory
  ): Promise<{ response: ChatResponse; updatedConversationHistory: ConversationHistory }> {
    console.log('🚀 Début de sendMessage avec:', {
      userMessage: userMessage.substring(0, 50) + '...',
      conversationHistoryLength: conversationHistory.messages.length,
      detectedLabelsCount: conversationHistory.detectedLabels?.length || 0
    });
    
    // Étape 1: Envoyer la conversation complète à l'agent backend
    const backendResponse = await this.sendToBackendAgent(conversationHistory, config, userMessage);
    console.log('🔵 Réponse du backend reçue:', backendResponse.content.substring(0, 100) + '...');
    
    // Sauvegarder les labels détectés dans la réponse du backend
    const backendLabels = this.detectLabels(backendResponse.content);
    console.log('🏷️ Labels détectés dans la réponse backend:', backendLabels);
    const updatedConversationHistory = this.saveDetectedLabels(backendLabels, 'backend', conversationHistory);
    
    // Étape 2: Construire le prompt système enrichi pour l'agent frontend
    const enrichedSystemPrompt = this.buildEnrichedSystemPrompt(config.systemPrompt, backendResponse.content, config.labelPrompts, updatedConversationHistory);
    
    const messages: ChatMessage[] = [];

    // Ajouter le prompt système enrichi
    if (enrichedSystemPrompt.trim()) {
      messages.push({
        role: 'system',
        content: enrichedSystemPrompt,
        timestamp: new Date(),
      });
    }

    // Ajouter tout l'historique de la conversation
    updatedConversationHistory.messages.forEach(message => {
      messages.push({
        role: message.role,
        content: message.content,
        timestamp: message.timestamp,
      });
    });

    // Construire le nouveau message utilisateur avec le contexte
    let fullUserMessage = userMessage;
    
    if (config.contextBefore.trim()) {
      fullUserMessage = `${config.contextBefore}\n\n${fullUserMessage}`;
    }
    
    if (config.contextAfter.trim()) {
      fullUserMessage = `${fullUserMessage}\n\n${config.contextAfter}`;
    }

    messages.push({
      role: 'user',
      content: fullUserMessage,
      timestamp: new Date(),
    });

    const frontendResponse = await this.makeRequest(messages, config);
    console.log('🔴 Réponse du frontend reçue:', frontendResponse.content.substring(0, 100) + '...');
    
    // Sauvegarder les labels détectés dans la réponse du frontend
    const frontendLabels = this.detectLabels(frontendResponse.content);
    console.log('🏷️ Labels détectés dans la réponse frontend:', frontendLabels);
    const finalConversationHistory = this.saveDetectedLabels(frontendLabels, 'frontend', updatedConversationHistory);
    
    console.log('✅ sendMessage terminé. Labels totaux en mémoire:', finalConversationHistory.detectedLabels?.length || 0);
    
    return { response: frontendResponse, updatedConversationHistory: finalConversationHistory };
  }

  public static async getEnrichedSystemPrompt(
    config: ChatConfig,
    conversationHistory: ConversationHistory,
    currentUserMessage?: string
  ): Promise<string> {
    try {
      const backendResponse = await this.sendToBackendAgent(conversationHistory, config, currentUserMessage);
      return this.buildEnrichedSystemPrompt(config.systemPrompt, backendResponse.content, config.labelPrompts, conversationHistory);
    } catch (error) {
      console.error('Erreur lors de la récupération du prompt système enrichi:', error);
      return config.systemPrompt;
    }
  }

  // Nouvelle méthode pour récupérer la réponse du backend pour le debug
  public static async getBackendResponse(
    config: ChatConfig,
    conversationHistory: ConversationHistory,
    currentUserMessage?: string
  ): Promise<string> {
    try {
      const backendResponse = await this.sendToBackendAgent(conversationHistory, config, currentUserMessage);
      return backendResponse.content;
    } catch (error) {
      console.error('Erreur lors de la récupération de la réponse backend:', error);
      return 'Erreur lors de la récupération de la réponse backend';
    }
  }

  private static async sendToBackendAgent(
    conversationHistory: ConversationHistory,
    config: ChatConfig,
    currentUserMessage?: string
  ): Promise<ChatResponse> {
    const messages: ChatMessage[] = [];

    // Préparer le prompt système avec remplacement de ****USER_DATA****
    let backendSystemPrompt = config.backendSystemPrompt;
    
    // Remplacer ****USER_DATA**** par le contenu de la mémoire des labels
    if (backendSystemPrompt.includes('****USER_DATA****')) {
      const memoryContent = this.buildMemoryContent(conversationHistory.detectedLabels);
      backendSystemPrompt = backendSystemPrompt.replace('****USER_DATA****', memoryContent);
    }

    // Ajouter le prompt système de l'agent backend
    if (backendSystemPrompt.trim()) {
      messages.push({
        role: 'system',
        content: backendSystemPrompt,
        timestamp: new Date(),
      });
    }

    // Ajouter tout l'historique de la conversation
    conversationHistory.messages.forEach(message => {
      messages.push({
        role: message.role,
        content: message.content,
        timestamp: message.timestamp,
      });
    });

    // Ajouter le message utilisateur actuel s'il est fourni
    if (currentUserMessage) {
      messages.push({
        role: 'user',
        content: currentUserMessage,
        timestamp: new Date(),
      });
    }

    return this.makeRequest(messages, config);
  }

  private static buildMemoryContent(detectedLabels: DetectedLabel[] | undefined): string {
    if (!detectedLabels || detectedLabels.length === 0) {
      return 'Aucun label détecté dans la mémoire pour le moment.';
    }

    const memoryContent = detectedLabels.map(label => {
      const sourceText = label.source === 'backend' ? 'Agent Backend' : 'Agent Frontend';
      const timestamp = label.timestamp.toLocaleString('fr-FR');
      return `[${label.label}] - Détecté par ${sourceText} le ${timestamp}`;
    }).join('\n');

    return `Mémoire des labels détectés:\n${memoryContent}`;
  }

  private static buildEnrichedSystemPrompt(
    originalSystemPrompt: string,
    backendAnalysis: string,
    labelPrompts: LabelPromptMapping[],
    conversationHistory: ConversationHistory
  ): string {
    // Regarder dans la mémoire des labels détectés au lieu de détecter dans la réponse du backend
    const memoryLabels = conversationHistory.detectedLabels || [];
    console.log('🔍 Labels trouvés dans la mémoire:', memoryLabels.map(dl => dl.label));
    
    // Construire processedBackendAnalysis uniquement à partir des prompts des labels trouvés dans la mémoire
    const promptsFromMemory: string[] = [];
    
    memoryLabels.forEach(detectedLabel => {
      const label = detectedLabel.label;
      const labelPrompt = labelPrompts.find(lp => lp.label === label);
      if (labelPrompt) {
        promptsFromMemory.push(labelPrompt.prompt);
        console.log(`✅ Prompt ajouté pour le label [${label}] (depuis la mémoire)`);
      } else {
        console.log(`❌ Aucun prompt trouvé pour le label [${label}] dans la configuration`);
      }
    });
    
    // Construire processedBackendAnalysis uniquement avec les prompts des labels de la mémoire
    const processedBackendAnalysis = promptsFromMemory.join('\n\n');

    return `${originalSystemPrompt}

=== ANALYSE DE L'AGENT BACKEND ===
${processedBackendAnalysis}
=== FIN DE L'ANALYSE ===

Utilise cette analyse pour enrichir ta réponse tout en respectant ton rôle principal.`;
  }

  // Nouvelle méthode pour sauvegarder les labels détectés
  public static saveDetectedLabels(
    labels: string[],
    source: 'backend' | 'frontend',
    conversationHistory: ConversationHistory
  ): ConversationHistory {
    console.log('💾 Sauvegarde des labels:', {
      labels,
      source,
      conversationHistoryExists: !!conversationHistory,
      currentDetectedLabels: conversationHistory.detectedLabels
    });
    console.log('💾 Labels à sauvegarder:', labels);
    console.log('💾 Source:', source);
    console.log('💾 ConversationHistory existe:', !!conversationHistory);
    console.log('💾 detectedLabels actuel:', conversationHistory.detectedLabels);
    
    if (labels.length === 0) {
      console.log('ℹ️ Aucun label à sauvegarder');
      return conversationHistory;
    }
    
    // S'assurer que detectedLabels existe
    if (!conversationHistory.detectedLabels) {
      console.log('🆕 Initialisation de detectedLabels');
      conversationHistory.detectedLabels = [];
    }
    
    // Filtrer les labels qui n'existent pas déjà
    const existingLabels = conversationHistory.detectedLabels.map(dl => dl.label);
    const newLabels = labels.filter(label => !existingLabels.includes(label));
    
    console.log('🔍 Labels existants:', existingLabels);
    console.log('🔍 Nouveaux labels uniques:', newLabels);
    
    if (newLabels.length === 0) {
      console.log('ℹ️ Tous les labels existent déjà, aucun ajout nécessaire');
      return conversationHistory;
    }
    
    const newDetectedLabels = newLabels.map(label => ({
      label,
      timestamp: new Date(),
      source
    }));
    
    console.log('📝 Nouveaux labels à ajouter:', newDetectedLabels);
    
    // Créer une nouvelle copie de conversationHistory avec les labels mis à jour
    const updatedConversationHistory = {
      ...conversationHistory,
      detectedLabels: [
        ...conversationHistory.detectedLabels,
        ...newDetectedLabels
      ]
    };
    
    console.log('✅ Labels sauvegardés avec succès:', {
      newLabels: newLabels,
      source,
      previousCount: conversationHistory.detectedLabels.length,
      newCount: updatedConversationHistory.detectedLabels.length,
      totalLabels: updatedConversationHistory.detectedLabels
    });
    console.log('✅ État final de detectedLabels:', updatedConversationHistory.detectedLabels);
    
    return updatedConversationHistory;
  }

  private static detectLabels(text: string): string[] {
    // Détecter les labels au format [label]
    const labelRegex = /\[([^\]]+)\]/g;
    const labels: string[] = [];
    let match;
    
    console.log('🔍 Détection des labels dans le texte:', text);
    console.log('🔍 Longueur du texte:', text.length);
    console.log('🔍 Regex utilisé:', labelRegex);
    
    while ((match = labelRegex.exec(text)) !== null) {
      const label = match[1].trim();
      console.log('🏷️ Label trouvé:', label, 'dans:', match[0]);
      console.log('🏷️ Match complet:', match);
      console.log('🏷️ Label après trim:', `"${label}"`);
      if (label && !labels.includes(label)) {
        labels.push(label);
        console.log('✅ Label ajouté à la liste:', label);
      } else if (labels.includes(label)) {
        console.log('⚠️ Label déjà présent:', label);
      } else {
        console.log('❌ Label vide ou invalide:', label);
      }
    }
    
    console.log('📋 Labels détectés au total:', labels);
    console.log('📋 Nombre de labels:', labels.length);
    return labels;
  }

  public static estimateTokens(text: string): number {
    // Estimation simple : ~4 caractères par token en moyenne
    return Math.ceil(text.length / 4);
  }

  public static async generateConversationSummary(messages: ChatMessage[]): Promise<{ summary: string; contactInfo: string }> {
    // Filtrer les messages système et créer un résumé de la conversation
    const conversationText = messages
      .filter(msg => msg.role !== 'system')
      .map(msg => `${msg.role === 'user' ? 'Utilisateur' : 'JudicIA'}: ${msg.content}`)
      .join('\n\n');

    const summaryPrompt = `Analyse cette conversation et fournis :
1. Un résumé en 3-5 mots maximum du sujet principal
2. Les coordonnées de la personne (nom, téléphone, email, adresse) si elles sont mentionnées

Format de réponse :
Résumé: [3-5 mots]
Coordonnées: [coordonnées si disponibles, sinon "Aucune coordonnée mentionnée"]

Conversation:
${conversationText}`;

    try {
      const response = await this.makeRequest([
        {
          role: 'system',
          content: 'Tu es un assistant spécialisé dans l\'analyse de conversations. Tu dois fournir des résumés concis et extraire les coordonnées de contact.',
          timestamp: new Date(),
        },
        {
          role: 'user',
          content: summaryPrompt,
          timestamp: new Date(),
        }
      ], {
        systemPrompt: '',
        backendSystemPrompt: '',
        temperature: 0.3,
        maxTokens: 200,
        model: 'gpt-4',
        contextBefore: '',
        contextAfter: '',
        labelPrompts: [],
      });

      const content = response.content;
      const summaryMatch = content.match(/Résumé:\s*(.+)/);
      const contactMatch = content.match(/Coordonnées:\s*(.+)/);

      return {
        summary: summaryMatch ? summaryMatch[1].trim() : 'Conversation générale',
        contactInfo: contactMatch ? contactMatch[1].trim() : 'Aucune coordonnée mentionnée'
      };
    } catch (error) {
      console.error('Erreur lors de la génération du résumé:', error);
      return {
        summary: 'Conversation générale',
        contactInfo: 'Aucune coordonnée mentionnée'
      };
    }
  }
} 